import { Button, Form, Table, Tooltip } from 'antd';
import { useMaterialRequestStore } from '../../../../../store/MaterialRequestStore';
import React, { useEffect, useState } from 'react';
import { useFetchData } from '../../../../../hooks';
import {
  BaseModel,
  FormField,
  PageableResponse,
  Proder,
  ProductModel,
  toProductMode,
} from '../../../../../types';
import { Resource } from '../../../../../lib/enums';
import { Movement } from '../../../../../types/MaterialRequest';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import WarningModal from '../../../../../components/ui/modal/WarningModal';
import InfoModal from '../../../../../components/ui/modal/InfoModal';
import ModalForm from '../../../../../components/ui/ModalForm';
import {
  useFetchProderProducts,
  useFetchProderSKUS,
} from '../../../../../hooks/useMaterialRequestHooks';

const MaterialRequestMovementEdit = () => {
  const [form] = Form.useForm();
  const materialRequestStore = useMaterialRequestStore();

  const [modalVisible, setModalVisible] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [uuidDelete, setUuidDelete] = useState('');
  const [proderId, setProderId] = useState<number | null>(null);
  const [productLoading, setProductLoading] = useState(false);
  const [productOptions, setProductOptions] = useState<
    ProductModel[] | undefined
  >([]);

  const [proderCode, setProderCode] = useState<string | null>(null);
  const [skuLoading, setSkuLoading] = useState(false);
  const [skuOptions, setSkuOptions] = useState<BaseModel[] | undefined>([]);

  const { data: proderData, isLoading: isProderLoading } = useFetchData<
    PageableResponse<Proder>
  >('/proders', {
    resource: Resource.FULL,
    length: parseInt('999999'),
  });

  const { data: productData, isLoading: isProductLoading } = useFetchData<
    PageableResponse<ProductModel>
  >('/product/products', {
    resource: Resource.FULL,
    length: parseInt('999999'),
  });

  const { data: uomData, isLoading: isUomLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('/product/uoms', {
    resource: Resource.MIN,
    search: '',
    length: '999999',
  });

  const { data: proderProductData, isLoading: isProderProductLoading } =
    useFetchProderProducts(proderId);
  const { data: proderSkuData, isLoading: isProderSkuLoading } =
    useFetchProderSKUS(proderCode);

  useEffect(() => {
    setProductLoading(isProductLoading);
    if (!isProductLoading) {
      setProductOptions(productData?.data);
    }
    if (!isProderProductLoading && proderProductData !== undefined) {
      setProductLoading(isProderProductLoading);
      setProductOptions(proderProductData);
    }

    setSkuLoading(isProderSkuLoading);
    if (!isProderSkuLoading) {
      setSkuOptions([]);
    }
    if (!isProderSkuLoading && proderSkuData !== undefined) {
      setSkuLoading(isProderSkuLoading);
      setSkuOptions(proderSkuData);
    }
  }, [
    isProductLoading,
    isProderProductLoading,
    productOptions,
    isProderSkuLoading,
    proderSkuData,
    skuOptions,
    proderCode,
  ]);

  const proderOptions = proderData?.data.map((item) => ({
    id: item.id,
    code: item.proderno,
    description: item.proderno,
  })) as BaseModel[];

  const handleOpenModal = () => {
    setModalVisible(true);
    form.resetFields();
  };

  const handleCloseModal = () => {
    setModalVisible(false);
  };

  const handleEditButtonClick = (record: Movement) => {
    setModalVisible(true);
    materialRequestStore.setMovement(record);
  };
  const handleDeleteButtonClick = (record: Movement) => {
    setUuidDelete(record.uuid);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteModalConfirm = () => {
    materialRequestStore.removeMovement(uuidDelete);
    setIsDeleteModalOpen(false);
    setIsInfoModalOpen(true);
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
  };

  const onValuesChange = (changedFields: any) => {
    if (changedFields.proderNo) {
      setProderId(changedFields.proderNo);
      setProderCode(
        proderData?.data?.find((obj) => obj.id === changedFields.proderNo)
          ?.proderno ?? null,
      );
      setProductLoading(true);
      setSkuLoading(true);
    }

    if (changedFields.product) {
      const product: ProductModel | undefined = productData?.data?.find(
        (obj) => {
          return obj.id == changedFields.id;
        },
      );
      form.setFieldValue('requestUom', product?.defaultUom?.id);
    }
  };

  const onFinish = (value: any) => {
    let product: BaseModel | undefined = productData?.data.find((obj) => {
      return obj.id == value.product;
    });
    // const productUom: BaseModel | undefined = uomData?.data?.find((obj) => {
    //   return obj.id == value.productUom;
    // });

    let requestUom: BaseModel | undefined = uomData?.data?.find((obj) => {
      return obj.id == value.requestUom;
    });

    if (product === undefined) {
      const recProduct = materialRequestStore.formData.movements.map((item) =>
        toProductMode(item.product!),
      );

      product = recProduct.find((obj) => {
        return obj.id == value.product;
      });
    }

    if (requestUom === undefined) {
      const recUoM = materialRequestStore.formData.movements.map(
        (item) => item.requestUom!,
      );

      requestUom = recUoM.find((obj) => {
        return obj.id == value.requestUom;
      });
    }

    const proderNo: BaseModel | undefined = proderOptions?.find((obj) => {
      return obj.id == value.proderNo;
    });

    const requestLine = {
      ...materialRequestStore.movements,
      ...value,
      product: product,
      // productUom: productUom,
      requestUom: requestUom,
      proderNo: proderNo?.code,
      uuid:
        materialRequestStore?.movements?.uuid === undefined ||
        materialRequestStore?.movements?.uuid === null ||
        materialRequestStore?.movements?.uuid === ''
          ? ''
          : materialRequestStore.movements?.uuid,
    };

    if (
      requestLine.id === undefined ||
      requestLine.id === null ||
      requestLine.id === ''
    ) {
      materialRequestStore.addMovement(requestLine);
    } else {
      materialRequestStore.editMovement(requestLine.uuid, requestLine);
    }
    setModalVisible(false);
  };

  const fields: FormField[] = [
    {
      name: 'proderNo',
      label: 'Proder',
      isMultiple: false,
      type: 'select',
      isLoading: isProderLoading,
      values: proderOptions,
    },
    {
      name: 'sku',
      label: 'SKU',
      isMultiple: false,
      type: 'select',
      values: skuOptions,
      isLoading: skuLoading,
      rules: [{ required: true, message: 'Please select SKU' }],
      defaultValue: materialRequestStore.movements?.sku,
    },
    {
      name: 'description',
      label: 'Description',
      type: 'input',
    },
    {
      name: 'product',
      label: 'Product',
      isMultiple: false,
      type: 'select',
      values: productOptions,
      isLoading: productLoading,
      rules: [{ required: true, message: 'Please select product' }],
      defaultValue: materialRequestStore.movements?.product?.id,
    },

    // {
    //   name: 'productUom',
    //   label: 'UoM',
    //   isMultiple: false,
    //   type: 'select',
    //   values: uomData?.data,
    //   isLoading: isUomLoading,
    //   defaultValue: materialRequestStore.movements?.productUom?.id,
    // },
    // {
    //   name: 'quantity',
    //   label: 'Quantity',
    //   type: 'numeric',
    //   rules: [{ required: true, message: 'Please enter quantity' }],
    //   defaultValue: materialRequestStore.movements?.quantity,
    // },

    {
      name: 'requestUom',
      label: 'Request UoM',
      isMultiple: false,
      type: 'select',
      values: uomData?.data,
      isLoading: isUomLoading,
      defaultValue: materialRequestStore.movements?.requestUom?.id,
    },
    {
      name: 'requestQty',
      label: 'Request Quantity',
      type: 'numeric',
      rules: [{ required: true, message: 'Please enter quantity' }],
      defaultValue: materialRequestStore.movements?.quantity,
    },
  ];
  return (
    <>
      <div className="py-2 px-4 flex justify-between">
        <div className={'text-base font-semibold leading-6'}>Movements</div>
        <Button onClick={handleOpenModal}>Add Movement</Button>
      </div>
      <div className="p-4">
        <Table
          rowKey={(record) => record.uuid}
          dataSource={materialRequestStore.formData.movements}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        >
          <Table.Column
            title="Product"
            dataIndex="product"
            render={(_text, record: Movement) => {
              return <span>{record.product?.description}</span>;
            }}
          />
          <Table.Column
            title="Code"
            dataIndex="productCode"
            render={(_text, record: Movement) => {
              return <span>{record.product?.code}</span>;
            }}
          />
          <Table.Column title="Description" dataIndex="description" />
          {/*<Table.Column*/}
          {/*  title="Product UoM"*/}
          {/*  dataIndex="productUoM"*/}
          {/*  render={(_text, record: Movement) => {*/}
          {/*    return <span>{record.productUom?.description}</span>;*/}
          {/*  }}*/}
          {/*/>*/}
          {/*<Table.Column title="Quantity" dataIndex="quantity" />*/}
          <Table.Column
            title="Request UoM"
            dataIndex="requestUom"
            render={(_text, record: Movement) => {
              return <span>{record.requestUom?.description}</span>;
            }}
          />
          <Table.Column title="Request Quantity" dataIndex="requestQty" />
          <Table.Column title="SKU" dataIndex="sku" />
          <Table.Column title="Proder No" dataIndex="proderNo" />
          <Table.Column
            title="Action"
            key="operation"
            fixed="right"
            render={(record: Movement) => {
              return (
                <div className="space-x-1">
                  <Tooltip title="Edit">
                    <Button
                      type="text"
                      icon={<EditOutlined style={{ color: '#4080fd' }} />}
                      size="middle"
                      onClick={() => handleEditButtonClick(record)}
                    />
                  </Tooltip>
                  <Tooltip title="Delete">
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      size="middle"
                      danger
                      onClick={() => handleDeleteButtonClick(record)}
                    />
                  </Tooltip>
                </div>
              );
            }}
          />
        </Table>
      </div>

      <WarningModal
        message={
          'This action cannot be undone and all associated data will be permanently removed from our system.'
        }
        onClose={handleDeleteModalClose}
        visible={isDeleteModalOpen}
        title={'Remove movement item?'}
        onConfirm={handleDeleteModalConfirm}
      />
      <InfoModal
        visible={isInfoModalOpen}
        message={'Movement item successfully removed'}
        onClose={handleInfoModalClose}
      />

      <ModalForm
        visible={modalVisible}
        onClose={handleCloseModal}
        formFields={fields}
        rowCol={3}
        form={form}
        onFinish={onFinish}
        onValuesChange={onValuesChange}
        data={materialRequestStore.movements}
        setData={materialRequestStore.setMovement}
      />
    </>
  );
};
export default MaterialRequestMovementEdit;
